import { Editor } from '@tiptap/core';
import { Fragment } from 'prosemirror-model';

// eslint-disable-next-line @typescript-eslint/ban-types
type NonNullish = Object;
type Settable<K, V> = { set: (key: K, value: V) => void };

const type = Symbol('type');

export type EditorContent = {
  /**
   * This ensures an object of this type can only be created via one of the
   * methods in this module
   */
  [type]: 'EditorContent';
  toString: () => string;
};

export function createEditorContent(html: string): EditorContent {
  return {
    [type]: 'EditorContent',
    toString: () => html,
  };
}

const weakMap = new WeakMap<Fragment, EditorContent>();

/**
 * Get the editor's content as an instance of EditorContent. Calling this
 * function with an editor that has the same content will always return the same
 * EditorContent instance. Besides enabling referential stability, this allows
 * us to memoize the toString function so that `editor.getHTML()` will never be
 * called more than once for a given content state.
 */
export function getEditorContent(editor: Editor): EditorContent {
  const content = editor.state.doc.content;
  return (
    weakMap.get(content) ??
    set(weakMap, content, {
      [type]: 'EditorContent',
      toString: memoize(() => editor.getHTML()),
    })
  );
}

function set<K, V, M extends Settable<K, V>>(map: M, key: K, value: V): V {
  map.set(key, value);
  return value;
}

function memoize<T extends NonNullish>(fn: () => T): () => T {
  let memoized: null | T;
  return () => memoized ?? (memoized = fn());
}
